import { DateTime } from "luxon";
import { cloneDeep } from "lodash-es";

import LocalForagePlugin, { authors, syncState } from "@/plugins/LocalForagePlugin";
import { authorsSyncKey } from "@/types/products/product-definitions";

import { goFetch } from "@/utils/network-utils";
import type { ApiFetchResponse, PersistedQueryPayload } from "@tmi/types/api/fetch";

import type { AuthorApiResponse, Authors } from "@tmi/types/api/content-models";
import type { Author } from "@tmi/types/entities";

import { consoleLoggerFactory, getDefaultLoggerOptions, LogLevels } from "@/types/console-logger";
const logger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

export function mapAuthorApiResponseToAuthorCatalog( response: ApiFetchResponse< Authors > ): Author[] {
    return [ ...( response.data.authorCatalog.authors
            .map( ( a: AuthorApiResponse ): Author => ( {
                id: a.id,
                publishedAt: a.publish_at,
                name: a.name,
                pictureUri: a.pic_uri,
                biography: a.bio,
                shortBiography: a.short_bio,
                slug: a.slug
            } ) )
    ) ];
}

export async function refreshAuthorsFromServer(): Promise< Author[] > {
    let allAuthors: Author[] = [];
    // Timing - testing to see how expensive each method is
    const t0: number = performance.now();

    let page: number = 0;
    const pageSize: number = 25;

    let authorsPayload: Author[] = [];

    do {
        const response =
            await goFetch< Authors, PersistedQueryPayload >( {
                endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                payload: {
                    id: '95c537b081ea7ea3ccfbbb34f257bfd4',
                    variables: {
                        skip: page,
                        limit: pageSize
                    }
                }
            } );

        if ( response?.data?.authorCatalog?.authors ) {
            authorsPayload = mapAuthorApiResponseToAuthorCatalog( response );

            const t1: number = performance.now();
            logger.info( `Fetching & mapping ${ authorsPayload.length } author items took ${ t1 - t0 } ms` );

            allAuthors = [ ...authorsPayload, ...allAuthors ];
        }

        // update the page #
        page++;
    } while ( (authorsPayload?.length ?? 0) >= pageSize );

    const t1: number = performance.now();
    logger.info( `Fetching, mapping & persisting ${ allAuthors.length } author items took ${ t1 - t0 } ms` );

    return allAuthors;
}

export async function loadAuthorMap( authorsMap: Map< string, Author > ): Promise< boolean > {
    let updated = false;
    try {
        const t0: number = performance.now();

        if ( typeof authors === 'undefined' || typeof syncState === 'undefined' ) {
            // Wait for the driver to settle
            LocalForagePlugin.initialize();
        }

        await syncState.ready();
        // Check for the next refresh interval
        const syncThresholdInMillis = await syncState.getItem< number >( authorsSyncKey );
        if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() )
        {
            const nextSyncThresholdInMillis = DateTime.utc().plus( { hours: 4 } ).toMillis();
            await syncState.setItem( authorsSyncKey, nextSyncThresholdInMillis );

            // Retrieve the latest collection of simple pages from the content service
            const authorsReceived: Author[] = await refreshAuthorsFromServer();

            // Store the items recently retrieved
            await authors.ready();

            if(authorsReceived.length > 0)
            {
                updated = true;

                for ( const author of authorsReceived ) {
                    await authors.setItem( author.id, cloneDeep( author ) );
                }

                const t1: number = performance.now();
                logger.info( `[ sync-worker ] Reset cache for ${ authorsMap.size } authors from server in ${ t1 - t0 } ms` );
            }
            else
            {
                const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                await syncState.setItem( authorsSyncKey, nextSyncThresholdInMillis );
            }
        }

        // Make sure map is empty
        authorsMap.clear();
        // Load whatever is in the local cache
        await authors.ready();
        // Iterate over all elements within IDB
        await authors.iterate( ( author: Author ) => {
            authorsMap.set( author.id, author );
            return;
        } );

    } catch ( err: any ) {
        logger.error( JSON.stringify( err.message ) );
    }

    return updated;
}
